/**
 * resolveFieldDescriptor — resolve the editor descriptor for a clicked field,
 * straight off the DOM.
 *
 * The build is self-describing: `expandEditable` stamps every editable node with
 * `data-builder-type` + `data-builder-options` (JSON) from its inline `editable:`
 * declaration. The descriptor is read directly from those attributes — no
 * `fields[]` manifest lookup. This is what lets a unit edit through a subroot /
 * fragment with no external manifest, and what made the legacy registry fallback
 * removable (ADR-001, lot R2).
 *
 * The returned descriptor mirrors the historical `fields[]` entry shape so the
 * click handler's branching (`editorType === 'text'`, modal options…) works
 * unchanged: `type` and `editorType` are both set to the declared type.
 *
 * @param {Element} fieldEl  The DOM element carrying data-builder-field/-type.
 * @param {string}  fieldKey The field key (data-builder-field value).
 * @returns {object|null} descriptor, or null when the node carries no type.
 */
export const resolveFieldDescriptor = (fieldEl, fieldKey) => {
  const domType = fieldEl?.getAttribute?.('data-builder-type')
  if (!domType) return null

  let options = {}
  const raw = fieldEl.getAttribute('data-builder-options')
  if (raw) {
    try {
      options = JSON.parse(raw)
    } catch (err) {
      console.warn('Builder: malformed data-builder-options for field', fieldKey, err)
    }
  }
  // key/type/editorType are authoritative (spread options first so they can't shadow them).
  return { ...options, key: fieldKey, type: domType, editorType: domType }
}

export default resolveFieldDescriptor
